import { paginatedSearch } from "actions/searchActions";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FilterSection, TableSection } from "views/CompanyDirectory/components";
import NeevayNetwork from "views/NeevayNetwork";
import FilterPanel from "views/NeevayNetwork/components/FilterPanel";
import GlobalSearchBar from "views/NeevayNetwork/components/GlobalSearchBar";
import SearchCategory from "views/NeevayNetwork/components/SearchCategory";
import SearchCategoryTable from "views/NeevayNetwork/components/SearchCategoryTable";
import SearchNew from "views/NeevayNetwork/components/SearchNew";
import Table from "views/NeevayNetwork/components/Table";

const SelectVendorsSlider = ({
  rfqId,
  sliderName = "NN",
  setSliderName,
  openSlider,
  setOpenSlider,
  handleSliderClose,
  invitedVendors,
  fromCreateRfq = false,
  fromRfqDetails = false,
  maxLimit,
  preselectedRecommendedVendors = []
}) => {
  const auth = useSelector((state) => state.auth);
  const rfqData = useSelector((state) => state.rfq);

  const dispatch = useDispatch();
  const user = auth?.user;
  const entityName = user?.userEntity?.entityName;
  const directoryName = user?.userEntity?.directoryName;

  const searchResult = useSelector((state) => state.search);
  const hasResults = useMemo(() => {
    return searchResult?.results && searchResult?.results.length > 0;
  }, [searchResult?.results]);

  const numFound = searchResult?.numFound ?? 0;
  const hasKeyword = !!searchResult?.searchKeyWord;
  const isLoading = searchResult?.isSearching;

  const showNoResults =
    hasKeyword && !isLoading && numFound === 0 && !hasResults;

  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showInitialCategories, setShowInitialCategories] = useState(true);
  const [showCategories, setShowCategories] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [hasAnimatedOnce, setHasAnimatedOnce] = useState(false);
  const isFirstLoad = useRef(true);
  const preselectedMarketplaceVendors = useMemo(
    () => preselectedRecommendedVendors.filter((vendor) => vendor?.source === "MARKETPLACE"),
    [preselectedRecommendedVendors]
  );
  const preselectedInternalVendors = useMemo(
    () => preselectedRecommendedVendors.filter((vendor) => vendor?.source !== "MARKETPLACE"),
    [preselectedRecommendedVendors]
  );

  const toggleDrawer = () => {
    setIsDrawerOpen(!isDrawerOpen);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const cdCloseClick = () => {
    if (fromCreateRfq) {
      navigate(`/rfqs/${rfqId}/Overview`)
      handleSliderClose()
    } else {
      handleSliderClose()
    }
  }

  const skipMarketplace = () => {
    setOpenSlider(false);
    navigate(`/rfqs/${rfqId}/Vendors`);
  };

  useEffect(() => {
    if (hasResults) {
      setShowInitialCategories(false);
    }
  }, [hasResults]);

  useEffect(() => {
    if (openSlider && sliderName === "NN") {
      if (preselectedMarketplaceVendors.length > 0) {
        setShowCategories(false);
        setShowContent(true);
        setHasAnimatedOnce(true);
        setIsAnimating(false);
        return;
      }

      setShowInitialCategories(true);
    }
  }, [openSlider, preselectedMarketplaceVendors.length, sliderName]);

useEffect(() => {
  if (openSlider && sliderName === "NN") {
    if (preselectedMarketplaceVendors.length > 0) {
      setShowCategories(false);
      setShowContent(true);
      setHasAnimatedOnce(true);
      setIsAnimating(false);
      return;
    }

    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      
    }

    const shouldAnimate =
      (hasResults || showNoResults) &&
      showCategories &&
      !hasAnimatedOnce;

    if (shouldAnimate) {
      setIsAnimating(true);
      setShowContent(false);

      setTimeout(() => {
        setShowCategories(false);
        setIsAnimating(false);
        setShowContent(true);
        setHasAnimatedOnce(true);
      }, 1000);

    } else if (!hasResults && !hasKeyword) {
      // only show Categories when no results AND no keyword search
      setShowCategories(true);
      setShowContent(false);

    } else if (hasResults) {
      setShowCategories(false);
      setShowContent(true);
    }
  }
}, [hasResults, showNoResults, showCategories, hasAnimatedOnce, openSlider, preselectedMarketplaceVendors.length, sliderName]);


  // Reset search when slider opens fresh
  useEffect(() => {
    if (openSlider && sliderName === "NN") {
      dispatch({ type: "SEARCH_RESET" });

      // Reset UI states
      setShowCategories(preselectedMarketplaceVendors.length === 0);
      setShowContent(preselectedMarketplaceVendors.length > 0);
      setHasAnimatedOnce(preselectedMarketplaceVendors.length > 0);
      setIsAnimating(false);
      isFirstLoad.current = true;
    }
  }, [dispatch, openSlider, preselectedMarketplaceVendors.length, sliderName]);


  return (
    <div
      className={`fixed z-50 top-0 right-0 h-full bg-gradient-to-r from-[#FFF0E4] to-[#E4E7FF] transition-transform duration-500 transform ${openSlider ? "translate-x-0" : "translate-x-full"
        }`}
      style={{ width: "calc(100% - 80px)" }}
    >

      {openSlider && sliderName == "NN" ? (
        <div>
          <div className="grid grid-cols-4 justify-between bg-white shadow-md items-center py-2 pl-5">
            <div className="flex items-center col-span-1">
              {!fromCreateRfq && <button className="mr-2 hover:bg-gray-100" onClick={handleSliderClose}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="text-gray-700 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>}
              <h1 className="text-base font-bold">Vendor Marketplace</h1>
            </div>
            <SearchNew />
            <div></div>
            {(fromCreateRfq || fromRfqDetails) && (
              <div className="col-span-1 flex justify-end mr-4">
                <button
                  className="text-sm font-medium text-blue-600 hover:underline underline"
                  onClick={skipMarketplace}
                >
                  Skip this step
                </button>
              </div>
            )}
          </div>

          {/* Floating Plugin Button - Only visible when there are results and drawer is closed */}
          {!showCategories && (
            <div onClick={toggleDrawer} className="fixed top-1/2 px-3 pt-1 pb-4 right-0 cursor-pointer bg-[#E4E7FF] border border-gray-300 shadow-lg hover:shadow-xl rounded-l-lg transform -translate-y-1/2 z-40">
              <span className="text-center text-lg font-bold text-gray-600 cursor-pointer">
                ‹‹‹
              </span>
              <button
                className="flex items-center justify-center font-medium writing-mode-vertical"
                style={{
                  writingMode: 'vertical-rl',
                  textOrientation: 'mixed'
                }}
              >
                <span className="flex items-center space-x-2">
                  <span className="whitespace-nowrap text-sm font-semibold text-gray-700">
                    Search by Category
                  </span>
                </span>
              </button>
            </div>
          )}

          {/* Drawer Overlay - Smoother fade */}
          <div
            className={`fixed inset-0 bg-black z-30 transition-all duration-700 ease-[cubic-bezier(0.4,0,0.2,1)] ${isDrawerOpen ? 'opacity-30 pointer-events-auto' : 'opacity-0 pointer-events-none'
              }`}
            onClick={closeDrawer}
          ></div>

          <div
            className={`fixed top-1/2 right-4 z-40 bg-white shadow-2xl rounded-lg overflow-y-auto ${isDrawerOpen
              ? 'opacity-100 pointer-events-auto max-h-[90vh]'
              : 'opacity-0 pointer-events-none max-h-0'
              }`}
            style={{
              width: isDrawerOpen ? '75%' : '0px',
              transform: isDrawerOpen
                ? 'translateY(-50%) scale(1)'
                : 'translateY(-50%) scale(0.95)',
              transformOrigin: 'right center',
              transition: isDrawerOpen
                ? 'all 300ms cubic-bezier(0.4,0,0.2,1), max-height 300ms cubic-bezier(0.4,0,0.2,1)'  // Fast open
                : 'all 1200ms cubic-bezier(0.4,0,0.2,1), max-height 1200ms cubic-bezier(0.4,0,0.2,1)' // Slow close
            }}
          >

            {/* Drawer Content */}
            <div
              className="overflow-y-auto transition-all duration-1000 ease-[cubic-bezier(0.4,0,0.2,1)]"
              style={{
                opacity: isDrawerOpen ? 1 : 0,
                transform: isDrawerOpen ? 'translateX(0)' : 'translateX(20px)',
              }}
            >
              <div className="flex justify-end">
                <button
                  onClick={closeDrawer}
                  className="text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full p-1 transition-colors duration-200"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    className="w-4 h-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              <div className="pb-4 px-4">
                <SearchCategoryTable
                  isInDrawer={true}
                  onClose={closeDrawer}
                  sliderName={sliderName}
                />
              </div>
            </div>
          </div>

          <div className="flex py-3 mt-2 pl-5 gap-x-5">
            {showCategories && !hasAnimatedOnce && (
              <div
                className={`w-full pr-5 transition-all ease-in-out ${isAnimating
                  ? 'transform translate-x-full opacity-0 scale-95'
                  : 'transform translate-x-0 opacity-100 scale-100'
                  }`}
                style={{ transitionDuration: '2200ms' }}
              >
                <SearchCategoryTable
                  isInDrawer={false}
                  onClose={closeDrawer}
                  sliderName={sliderName}
                />
              </div>
            )}

            {showCategories && hasAnimatedOnce && !hasResults && (
              <div className="w-full pr-5">
                <SearchCategoryTable
                  isInDrawer={false}
                  onClose={closeDrawer}
                  sliderName={sliderName}
                />
              </div>
            )}

            {/* 🔥 NO RESULTS FOUND BLOCK (Animated Slide-In) */}
            {showNoResults && !showCategories && (
              <div
                className={`
                    flex flex-col items-center justify-center h-[calc(100vh-120px)] w-full p-10
                    transition-all ease-in-out transform
                    ${isAnimating ? "translate-x-full opacity-0" : "translate-x-0 opacity-100"}
                  `}
                style={{ transitionDuration: "1200ms" }}
              >
                <img
                  src="https://storagereponeevaydevcdn.blob.core.windows.net/business/Search_image.png"
                  className="h-[116px] w-[122px] mb-6"
                  alt="No results"
                />

                <p className="text-md font-bold text-[#3C3C3C] text-center">
                  No results found for{" "}
                  <span className="text-[#FD6600]">"{searchResult.subCategory}"</span> in{" "}
                  <span className="text-[#FD6600]">{searchResult.regionString}</span>
                </p>

                <p className="text-sm text-gray-600 mt-2 text-center">
                  Try selecting a different city or browse other subcategories
                </p>
              </div>
            )}


            {showContent && (hasResults || preselectedMarketplaceVendors.length > 0) && (
              <>
                {(hasResults || searchResult?.selectedFacets?.length > 1) && (
                  <div className="w-[260px]">
                    <FilterPanel />
                  </div>
                )}

                <div
                  style={{ height: "calc(100vh - 120px)" }}
                  className="flex-grow overflow-x-auto pr-2"
                >
                  <Table
                    isSliderView={true}
                    rfqId={rfqId}
                    handleSliderClose={handleSliderClose}
                    invitedVendors={rfqData?.rfq?.suppliers || invitedVendors}
                    maxLimit={maxLimit}
                    preselectedVendors={preselectedMarketplaceVendors}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="flex h-screen p-3 pr-0 gap-7">
          <div className="h-full flex flex-col" style={{ width: "260px" }}>
            <div className="p-4 flex">
              <button className="mr-2 hover:bg-gray-100" onClick={cdCloseClick}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="text-gray-700 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <p className="font-bold text-base">Internal Vendors</p>
            </div>
            <div className="flex-grow pl-2 overflow-x-auto overflow-y-auto">
              <FilterSection />
            </div>
          </div>

          <div className="flex-grow overflow-x-auto overflow-y-auto pr-2">
            <TableSection
              isSliderView={true}
              rfqId={rfqId}
              handleSliderClose={handleSliderClose}
              invitedVendors={rfqData?.rfq?.suppliers || invitedVendors}
              setOpenSlider={setOpenSlider}
              setSliderName={setSliderName}
              maxLimit={maxLimit}
              preselectedVendors={preselectedInternalVendors}
            />
          </div>
        </div>
      )}

      <style jsx>{`
        .writing-mode-vertical {
          writing-mode: vertical-rl;
          text-orientation: mixed;
        }
        
        /* Enhanced smoothness */
        * {
          box-sizing: border-box;
        }
        
        /* Force GPU acceleration for smoother animations */
        .transform {
          will-change: transform;
        }
        
        .opacity {
          will-change: opacity;
        }
      `}</style>
    </div>
  );
};

export default SelectVendorsSlider;
